import type {
  SavingsBank,
  SavingsBankAssetUpload,
  SavingsBankPayload,
  SavingsInstructionFileDirection,
  SavingsInstructionFileRun
} from '@interfaces/Savings.interface';
import type { SavingsInstruction } from '@interfaces/SavingsInstruction.interface';
import {
  SAVINGS_API_VERSION_HEADERS,
  SavingsReferenceResourceClient,
  toSavingsListResponse
} from '../savings-reference-resource';
import type { ListDataParams, ListDataResponse } from '../types';

export class ApiSavingsBanks extends SavingsReferenceResourceClient<
  SavingsBank,
  SavingsBankPayload
> {
  protected readonly path = '/savings-banks';
  protected readonly searchFilter = 'name';

  async get(id: number): Promise<SavingsBank> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async praNames(): Promise<string[]> {
    const res = await this.client.get(`${this.path}/pra-names`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data ?? [];
  }

  /** The instructions the bank's next instruction file would carry. */
  async listReadyInstructions(
    bankId: number,
    direction: SavingsInstructionFileDirection,
    listData: ListDataParams
  ): Promise<ListDataResponse<SavingsInstruction>> {
    const res = await this.client.getList(
      `${this.path}/${bankId}/ready-instructions?direction=${direction}`,
      listData,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return toSavingsListResponse<SavingsInstruction>(res.data);
  }

  /** Today's instruction file runs for the bank. */
  async instructionFileRuns(
    bankId: number
  ): Promise<SavingsInstructionFileRun[]> {
    const res = await this.client.get(
      `${this.path}/${bankId}/instruction-files`,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data ?? [];
  }

  /** Generates today's file; the run carries a short-lived download link. */
  async createInstructionFile(
    bankId: number,
    direction: SavingsInstructionFileDirection
  ): Promise<SavingsInstructionFileRun> {
    const res = await this.client.post(
      `${this.path}/${bankId}/instruction-files`,
      { direction },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  /** Deletes one of today's runs, freeing its instructions for the next file. */
  async deleteInstructionFile(bankId: number, runId: number): Promise<void> {
    await this.client.delete(
      `${this.path}/${bankId}/instruction-files/${runId}`,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
  }

  async updateAssets(
    id: number,
    upload: SavingsBankAssetUpload
  ): Promise<SavingsBank> {
    const form = new FormData();
    form.append('_method', 'PUT');
    upload.remove.forEach((key) => form.append(`remove_${key}`, '1'));
    Object.entries(upload.files).forEach(([key, file]) => {
      if (file) form.append(key, file);
    });

    const res = await this.client.post(`${this.path}/${id}/assets`, form, {
      headers: {
        ...SAVINGS_API_VERSION_HEADERS,
        'Content-Type': 'multipart/form-data'
      }
    });
    return res.data.data;
  }
}

export { savingsBanksQueryKeys } from './query-keys';
export {
  useSavingsBankInstructionFileRunsQuery,
  useSavingsBankPraNamesQuery,
  useSavingsBankQuery,
  useSavingsBanksListQuery
} from './use-savings-banks-queries';
export {
  useSavingsBankInstructionFileCreateMutation,
  useSavingsBankInstructionFileDeleteMutation,
  useSavingsBanksCreateMutation,
  useSavingsBanksDeleteMutation,
  useSavingsBanksUpdateAssetsMutation,
  useSavingsBanksUpdateMutation
} from './use-savings-banks-mutations';
export type {
  SavingsBankInstructionFileCreateVariables,
  SavingsBankInstructionFileDeleteVariables,
  SavingsBanksUpdateVariables
} from './use-savings-banks-mutations';
